'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { AddToCart } from './AddToCart';
import { useSelectedVariant, type PdpVariant } from '@/lib/variant-store';
import { formatCentsClient } from '@/lib/format';

// Slim sticky bottom bar that slides up on mobile once the in-page Add-to-cart
// has scrolled out of view. Uses the same `#np-buy-sentinel` element the
// desktop bar observes — placed in PDP just above the inline AddToCart.
// Honors prefers-reduced-motion (Tailwind motion-safe variant) so users with
// reduced motion get a snap rather than a slide.

export function StickyMobileBuyBar({
  productId,
  slug,
  productName,
  image,
  brand,
  category,
  variants,
}: {
  productId:   string;
  slug:        string;
  productName: string;
  image?:      string;
  brand?:      string;
  category?:   string;
  variants:    PdpVariant[];
}) {
  const [show, setShow] = useState(false);
  const pathname = usePathname();
  const resolved = useSelectedVariant(variants);

  useEffect(() => {
    const el = document.getElementById('np-buy-sentinel');
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          // Sentinel sits just above the inline Add-to-cart. Once it scrolls
          // above the viewport, the inline CTA is leaving / has left view —
          // surface the sticky bar.
          setShow(e.boundingClientRect.bottom < 0);
        }
      },
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Defensive route guard: bar component is currently mounted only on PDP,
  // but we belt-and-suspender against future global mounting.
  if (pathname?.startsWith('/cart') || pathname?.startsWith('/checkout')) return null;

  // Size label + price shown in the bar's middle column.
  const prices = variants.map((v) => v.priceCents);
  const lo = Math.min(...prices);
  const hi = Math.max(...prices);
  const selectedSizeLabel = resolved.hasVariants ? resolved.variant?.title : undefined;
  const displayPrice = resolved.priceCents != null
    ? formatCentsClient(resolved.priceCents)
    : lo === hi ? formatCentsClient(lo) : `${formatCentsClient(lo)} – ${formatCentsClient(hi)}`;

  return (
    <div
      className={[
        'md:hidden fixed bottom-0 left-0 right-0 z-40',
        'bg-bone/95 backdrop-blur border-t border-ink/15',
        'px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]',
        'motion-safe:transition-transform motion-safe:duration-200',
        show ? 'translate-y-0' : 'translate-y-full',
      ].join(' ')}
      aria-hidden={!show}
    >
      <div className="flex items-center gap-3 max-w-md mx-auto">
        {image && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={image}
            alt=""
            width={44}
            height={44}
            className="w-11 h-11 object-contain p-[6%] bg-ink/5 rounded-sm shrink-0"
          />
        )}
        <div className="min-w-0 flex-1">
          {brand && (
            <p className="text-[9px] uppercase tracking-[0.12em] text-muted-strong leading-none truncate">{brand}</p>
          )}
          <p className="text-[12px] text-ink leading-tight truncate font-medium mt-0.5">{productName}</p>
          <p className="text-[13px] text-ink font-numerals mt-0.5 leading-none">
            {selectedSizeLabel && <span className="text-muted-strong text-xs uppercase tracking-[0.1em] mr-2">{selectedSizeLabel}</span>}
            {displayPrice}
          </p>
        </div>
        <AddToCart
          productId={productId}
          slug={slug}
          productName={productName}
          brand={brand}
          image={image}
          category={category}
          variants={variants}
          compact
        />
      </div>
    </div>
  );
}
